import { useMessageSource } from 'src/i18n/useMessageSource'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Autocomplete, Box, Button, Grid2, Stack, Typography } from '@mui/material'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import { MilestoneConfigurationGrid } from 'src/screens/administration/configuration-management/milestones-configuration/MilestoneConfigurationGrid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
} from 'src/shared/constants/table-settings-constants'
import { Milestone_Config_Bool_Exp, Milestone_Config_Order_By, Query_Root } from 'src/@types/graphql'
import { queryMilestoneConfigs } from 'src/screens/administration/configuration-management/milestones-configuration/milestoneConfigQuerties'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import TextField from '@mui/material/TextField'
import { Option } from 'src/shared/form/control'
import { PROJECT_TYPE } from 'src/shared/constants/constants'
import {
  MILESTONE_RESPONSIBLE_TYPE,
  MILESTONE_TYPE,
  MILESTONE_TYPE_FOR_PROCESS,
  MILESTONE_TYPE_TYPE,
} from 'src/shared/constants/milestone-constants'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
  useTableSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'
import {
  getMilestoneConfigDueDate,
  getMilestoneConfigProcessOptions,
  mapMilestoneResponsibleTypesToOptions,
  mapMilestoneTypesToOptions,
  MILESTONE_CONFIG_PROCESSES,
} from 'src/screens/administration/configuration-management/milestones-configuration/MilestoneConfigUtils'
import {
  getOptionsForFilter,
  getTranslationForMilestoneResponsibleType,
  getTranslationForMilestoneType,
  getTranslationForProcess,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'

const MILESTONE_CONFIG_TABLE_NAME = 'MILESTONE_CONFIG'

type MilestoneConfigFilterValues = SpecificConfigFilter<'MILESTONE_CONFIG'>

const defaultMilestoneConfigTableSettings: ConfigurationTablePageSortSettings = {
  ...DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
  sortModel: [
    {
      field: 'type',
      sort: 'asc',
    },
  ],
}

export const MilestonesConfigurationIndexPage = () => {
  const { getMessage } = useMessageSource()

  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  // filter options
  const getMilestoneProcessOptions = useCallback((typeFilter: Option | null): Option[] => {
    const milestoneConfigProcessOptions = getMilestoneConfigProcessOptions(getMessage)

    // If no milestone type is selected, show all processes
    if (!typeFilter?.value) {
      return getOptionsForFilter(milestoneConfigProcessOptions)
    }

    // Filter processes to only those that include the selected milestone type
    const filteredProcesses = (Object.entries(MILESTONE_TYPE_FOR_PROCESS) as [PROJECT_TYPE, MILESTONE_TYPE_TYPE[]][])
      .filter(
        ([projectType, milestones]) =>
          MILESTONE_CONFIG_PROCESSES.includes(projectType) &&
          milestones.includes(typeFilter.value as MILESTONE_TYPE_TYPE),
      )
      .map(([projectType]) => {
        const processOpt = milestoneConfigProcessOptions.find((opt) => opt.value === projectType)
        if (!processOpt) {
          throw new Error('Invalid process type to get milestones for')
        }

        return processOpt
      })

    return getOptionsForFilter(filteredProcesses)
  }, [])

  const getMilestoneTypeOptions = useCallback((processFilter: Option | null): Option[] => {
    const projectType = processFilter?.value as PROJECT_TYPE

    const options: Option[] = []

    if (projectType && MILESTONE_TYPE_FOR_PROCESS[projectType]) {
      options.push(...mapMilestoneTypesToOptions(MILESTONE_TYPE_FOR_PROCESS[projectType]))
    } else {
      // if no project is selected show all milestone types
      options.push(...mapMilestoneTypesToOptions(Object.values(MILESTONE_TYPE)))
    }

    return getOptionsForFilter(options)
  }, [])

  const responsibleTypeOptions: Option[] = useMemo(
    () => getOptionsForFilter(mapMilestoneResponsibleTypesToOptions(Object.values(MILESTONE_RESPONSIBLE_TYPE))),
    [],
  )

  const defaultMilestoneConfigFiltersValues = useMemo(
    () => ({
      milestoneProcessFilter: getMilestoneProcessOptions(null)[0],
      milestoneTypeFilter: getMilestoneTypeOptions(null)[0],
      milestoneResponsibleFilter: responsibleTypeOptions[0],
    }),
    [getMilestoneProcessOptions, getMilestoneTypeOptions, responsibleTypeOptions],
  )

  // grid and filter settings
  const [gridDataSettings, setGridDataSettings] = useState<ConfigurationGridDataSettings>(
    DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  )
  const [tableSettings, setTableSettings] = useTableSettingsFromLocalStorage(
    MILESTONE_CONFIG_TABLE_NAME,
    defaultMilestoneConfigTableSettings,
  )
  const [milestoneConfigFilters, setMilestoneConfigFilters] = useFilterSettingsFromLocalStorage(
    defaultMilestoneConfigFiltersValues,
  )

  // table state
  const fetchRows = useCallback(
    async (newTableSettings: ConfigurationTablePageSortSettings, newTableFilter: MilestoneConfigFilterValues) => {
      // paging
      const limit = newTableSettings.paginationModel.pageSize
      const offset = newTableSettings.paginationModel.page * limit

      // order
      const orderBy = [
        {
          type: newTableSettings.sortModel?.find((sortItem) => sortItem.field === 'type')?.sort,
        },
        {
          id: 'asc',
        },
      ] as Array<Milestone_Config_Order_By>

      // filter condition
      const milestoneConfigCondition: Milestone_Config_Bool_Exp = { _and: [] }
      if (newTableFilter.milestoneProcessFilter.value != null) {
        const selectedProcess = newTableFilter.milestoneProcessFilter.value as string
        milestoneConfigCondition._and?.push({ process: { _eq: selectedProcess } })
      }
      if (newTableFilter.milestoneTypeFilter.value != null) {
        const selectedType = newTableFilter.milestoneTypeFilter.value as string
        milestoneConfigCondition._and?.push({ type: { _eq: selectedType } })
      }
      if (newTableFilter.milestoneResponsibleFilter.value != null) {
        const selectedResponsible = newTableFilter.milestoneResponsibleFilter.value as string
        milestoneConfigCondition._and?.push({ initial_responsible_type: { _eq: selectedResponsible } })
      }

      const { data, error } = await urqlClient
        .query<
          {
            milestone_config: Query_Root['milestone_config']
            milestone_config_aggregate: Query_Root['milestone_config_aggregate']
          },
          {
            milestoneConfigCondition: Milestone_Config_Bool_Exp
            offset: number
            limit: number
            orderBy: Milestone_Config_Order_By | Milestone_Config_Order_By[]
          }
        >(queryMilestoneConfigs, {
          milestoneConfigCondition,
          offset,
          limit,
          orderBy,
        })
        .toPromise()

      if (error || !data?.milestone_config || !data?.milestone_config_aggregate) {
        throw new Error('Resource not found')
      }

      // map result
      const fetchedRows = data.milestone_config.map((mc) => {
        const due = getMilestoneConfigDueDate(mc, getMessage, locale)

        return {
          id: mc.id,
          type: mc.type,
          process: mc.process,
          description: mc.description[locale],
          status: mc.active,
          frequency: mc.frequency,
          due: due,
          responsible: mc.initial_responsible_type,
        }
      })
      const fetchedRowsCount = data.milestone_config_aggregate.aggregate?.count ?? 0

      return {
        fetchedRows,
        fetchedRowsCount,
      }
    },
    [locale, urqlClient, getMessage],
  )

  const fetchRowsAndUpdateTableSettings = useCallback(
    (newTableSettings: ConfigurationTablePageSortSettings, newTableFilter: MilestoneConfigFilterValues) => {
      setGridDataSettings((prevData) => ({
        ...prevData,
        gridLoading: true,
      }))

      fetchRows(newTableSettings, newTableFilter)
        .then((res) => {
          setGridDataSettings({
            rows: res.fetchedRows,
            rowsCount: res.fetchedRowsCount,
            gridLoading: false,
          })
        })
        .catch((_) => {
          notificationService.operationFailed()
          setGridDataSettings((prevData) => ({
            ...prevData,
            gridLoading: false,
          }))
        })
    },
    [fetchRows, notificationService],
  )

  // filter handlers
  const handleTableFilterChange = useCallback(
    (newValue: Option | null, filterName: keyof MilestoneConfigFilterValues, defaultValue: Option) => {
      if ((newValue?.value || null) !== milestoneConfigFilters[filterName].value) {
        setTableSettings({
          ...tableSettings,
          paginationModel: { ...tableSettings.paginationModel, page: 0 },
        })

        setMilestoneConfigFilters({
          ...milestoneConfigFilters,
          [filterName]: newValue ?? defaultValue,
        })
      }
    },
    [milestoneConfigFilters, tableSettings, setMilestoneConfigFilters, setTableSettings],
  )

  const handleTableFilterReset = useCallback(() => {
    setMilestoneConfigFilters(defaultMilestoneConfigFiltersValues)
    setTableSettings({
      ...tableSettings,
      paginationModel: { ...tableSettings.paginationModel, page: 0 },
    })
  }, [defaultMilestoneConfigFiltersValues, tableSettings, setMilestoneConfigFilters, setTableSettings])

  useEffect(() => {
    fetchRowsAndUpdateTableSettings(tableSettings, milestoneConfigFilters)
  }, [fetchRowsAndUpdateTableSettings, milestoneConfigFilters, tableSettings])

  const currentProcessOptions = getMilestoneProcessOptions(milestoneConfigFilters.milestoneTypeFilter)
  const currentTypeOptions = getMilestoneTypeOptions(milestoneConfigFilters.milestoneProcessFilter)

  return (
    <ScreenLayout
      title={`${getMessage('label.navigation.configuration.milestones')} ${getMessage('label.configuration')}`}
      hasSecondLevelNavigation={false}
    >
      <PageLayout>
        <Stack spacing={5}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 1,
              padding: 2,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary" fontWeight="bold">
                {getMessage('label.grid.filter')}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={4}>
                  <Autocomplete
                    options={currentProcessOptions}
                    value={milestoneConfigFilters.milestoneProcessFilter}
                    onChange={(_, newVal) =>
                      handleTableFilterChange(
                        newVal,
                        'milestoneProcessFilter',
                        defaultMilestoneConfigFiltersValues.milestoneProcessFilter,
                      )
                    }
                    getOptionLabel={(opt) => getTranslationForProcess(getMessage, opt.value)}
                    renderInput={(params) => <TextField {...params} label={getMessage('label.process')} />}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <Autocomplete
                    options={currentTypeOptions}
                    value={milestoneConfigFilters.milestoneTypeFilter}
                    onChange={(_, newVal) =>
                      handleTableFilterChange(
                        newVal,
                        'milestoneTypeFilter',
                        defaultMilestoneConfigFiltersValues.milestoneTypeFilter,
                      )
                    }
                    getOptionLabel={(opt) => getTranslationForMilestoneType(getMessage, opt.value)}
                    renderInput={(params) => <TextField {...params} label={getMessage('label.type')} />}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <Autocomplete
                    options={responsibleTypeOptions}
                    value={milestoneConfigFilters.milestoneResponsibleFilter}
                    onChange={(_, newVal) =>
                      handleTableFilterChange(
                        newVal,
                        'milestoneResponsibleFilter',
                        defaultMilestoneConfigFiltersValues.milestoneResponsibleFilter,
                      )
                    }
                    getOptionLabel={(opt) => getTranslationForMilestoneResponsibleType(getMessage, opt.value)}
                    renderInput={(params) => (
                      <TextField {...params} label={getMessage('label.milestone.config.responsible')} />
                    )}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <Button variant="text" startIcon={<FilterAltOffOutlinedIcon />} onClick={handleTableFilterReset}>
                    {getMessage('label.configuration.criteria.group.grid.filter.reset.button')}
                  </Button>
                </Grid2>
              </Grid2>
            </Stack>
          </Box>

          <MilestoneConfigurationGrid
            dataSettings={gridDataSettings}
            tableSettings={tableSettings}
            setTableSettings={setTableSettings}
          />
        </Stack>
      </PageLayout>
    </ScreenLayout>
  )
}
